import { Nutrients, NutrientTargets } from './food';
import { DateTime } from 'luxon';
import { Ingredient } from './ingredient';
import { assureIdForEntry, firstFreeLogEntryId, getEntryLocalTimeInDesiredTimeZone, LogRecordState, LogRecordType, Log_EntryBase } from './user-log';

export interface Log_Meal extends Log_EntryBase {
  type: LogRecordType.Meal;
  ingredients: Ingredient[]; // this was previously called mealFoods
  nutrients: Nutrients;
  /** Targets that are used for meal planning. As one meal is planned and it over/under-shoots, the rest use different numbers to better fit the daily targets.
   * NOTE: This was previously called "displayTargets", so it needs to be handled for backward compatibility */
  planningTargets: NutrientTargets;
  targets: NutrientTargets /** Original targets from the fitness plan. */;
  /** Set if this meal was generated from some recipe or saved meal. */
  basedOnFoodId?: string;
  cutMeal: boolean;
  /** Set to true if this meal was auto-planned (via Auto Plan feature). */
  autoPlanned?: boolean;
}

export function createMealEntry(date_local: DateTime, name: string): Log_Meal {
  let entry = {} as Log_Meal;
  entry.name = name;
  entry.time_utc = date_local.toUTC().toISO();
  entry.timeOffset_min = date_local.offset;
  entry.type = LogRecordType.Meal;
  entry.state = LogRecordState.Scheduled;

  entry.ingredients = [];
  entry.nutrients = {};
  entry.targets = {};
  entry.planningTargets = {};

  assureIdForEntry(entry);
  return entry;
}

/**
 * Which meal slot an entry belongs to on its day — "Meal 1", "Meal 2" and so on.
 *
 * A meal has no stable per-slot identifier of its own: `Log_Meal` carries only a name and a
 * time-derived `_id`. The name is what the app shows and what it groups by. A meal with no name
 * falls back to its `_id`, which is unique, so two unnamed meals are never treated as one.
 *
 * Takes only the two fields it reads, so a caller holding a meal's identity without the whole
 * document -- a meal still being built, which is not saved anywhere yet -- can still ask which
 * slot it belongs to.
 */
export function mealSlotKey(meal: Pick<Log_Meal, '_id' | 'name'>): string {
  return meal.name ? `name:${meal.name}` : `id:${meal._id}`;
}

/**
 * Mark a meal `Planned` once food has been chosen for it, if it still says `Scheduled`. The one
 * copy of this rule: the day list, the edit-meal screen and the meal-plan page all call it, so
 * they cannot drift apart again (df-a57a6f1ef132). A meal left `Scheduled` with food
 * on it used to read as an empty placeholder, which a schedule rebuild may delete
 * (df-c87743b6cc87). Any other state is the user's own and is left alone.
 */
export function markMealPlannedIfItHoldsFood(meal: Log_Meal) {
  const anyFoodSelected = Array.isArray(meal.ingredients) && meal.ingredients.length > 0;
  if (anyFoodSelected && meal.state === LogRecordState.Scheduled) {
    meal.state = LogRecordState.Planned;
  }
}

/**
 * Would this meal have added anything to its day's total?
 *
 * Answered on the nutrient VALUES rather than on `state`, so a meal that somehow arrived with
 * food on it still counts even if its state says otherwise. Matches what the day's summing loop
 * actually adds up: it skips a `Skipped` meal and adds every nutrient of the rest.
 *
 * This is what tells a duplicate that cost the user real food apart from a leftover placeholder
 * that could not have changed the total, which decides how loudly the drop is reported.
 */
export function mealWouldCountTowardDayTotal(meal: Log_Meal): boolean {
  if (meal.state === LogRecordState.Skipped) return false;
  return Object.values(meal.nutrients || {}).some(amount => !!amount);
}

/** The fields of a meal that {@link pickOneMealPerSlot} reads. */
export type MealSlotCandidate = Pick<Log_Meal, '_id' | 'name' | 'state' | 'time_utc' | 'timeOffset_min'>;

/**
 * Reduce a day's meals to at most one per slot, and say which ones were left out.
 *
 * A day can legitimately hold two documents for the same meal, because a log entry holding food
 * a user typed in is never deleted. Every place that shows or totals a day must therefore apply
 * this one rule, or they disagree with each other. That includes the calendar's day marker, which
 * reads only a few fields of each meal and so passes those rather than whole documents (#6281).
 *
 * Which one wins: a meal holding user data beats an empty placeholder, and otherwise the later
 * one wins. The ordering is done HERE rather than taken from the caller, since callers arrive in
 * different orders; ties fall back to `_id` so the answer depends only on the meals themselves.
 *
 * `referenceTime` only supplies a time zone to read the meals' wall-clock times in; any time on
 * the day being shown will do.
 *
 * See `design-docs/sync-integrity.md`, "One Rule for Which Day an Entry Belongs To, and One Meal
 * per Slot", for what disagreeing cost a customer (#4728).
 */
export function pickOneMealPerSlot<T extends MealSlotCandidate>(meals: T[], referenceTime: DateTime): { kept: T[]; dropped: T[] } {
  const holdsUserData = (m: T) => m.state !== LogRecordState.Scheduled;

  const inSlotOrder = [...meals].sort((a, b) => {
    const aTime = getEntryLocalTimeInDesiredTimeZone(a, referenceTime).toMillis();
    const bTime = getEntryLocalTimeInDesiredTimeZone(b, referenceTime).toMillis();
    if (aTime !== bTime) return aTime - bTime;
    return (a._id || '') < (b._id || '') ? -1 : 1;
  });

  const winnerBySlot = new Map<string, T>();
  for (const meal of inSlotOrder) {
    const key = mealSlotKey(meal);
    const previous = winnerBySlot.get(key);
    // take it if the slot is empty, if it holds data the current winner does not, or if the
    // current winner is no better (equal category, and this one is later in the sorted order)
    if (!previous || holdsUserData(meal) || !holdsUserData(previous)) {
      winnerBySlot.set(key, meal);
    }
  }

  const kept: T[] = [];
  const dropped: T[] = [];
  for (const meal of meals) {
    (winnerBySlot.get(mealSlotKey(meal)) === meal ? kept : dropped).push(meal);
  }
  return { kept, dropped };
}

/**
 * Was this meal added by hand, rather than built by the schedule?
 *
 * Read from the id, which names the instant the meal was created at. The schedule builds meals on
 * whole minutes and, when that id is taken, only moves it forward a millisecond at a time
 * (`firstFreeLogEntryId`), so its ids sit in the first fraction of a second of a minute. A meal
 * added by hand takes the real clock, seconds and milliseconds included. A hand-added meal that
 * happens to land in that first tenth of a second reads as schedule-built and is left alone, and
 * so is a meal whose id is damaged and names no instant: the safe answer is to change nothing.
 */
export function mealWasAddedByHand(meal: Pick<Log_Meal, '_id'>): boolean {
  const idInstant = /^log\/([\d\-:T.]+Z)-/.exec(meal._id || '')?.[1];
  const idTime = idInstant ? DateTime.fromISO(idInstant, { zone: 'utc' }) : undefined;
  if (!idTime?.isValid) return false;
  return idTime.second !== 0 || idTime.millisecond >= 100;
}

/**
 * The first name for a hidden meal that no meal of the day uses (#6207).
 *
 * A "Meal N" name takes the "Meal" number one past the highest the day uses, so it never takes a
 * name a slot is about to hand out. This differs from the schedule rebuild's `renameMealsLeftOffSlots`,
 * which starts one past the slot count and takes the first name nobody uses: with Meal 1-4 and
 * Meal 7 on a 4-slot day, that gives "Meal 5" and this gives "Meal 8". Any other name gets " 2", " 3", ... after its base, a trailing number stripped first so
 * a hidden "Snack 2" becomes "Snack 3" rather than "Snack 2 2". The stored name is already in the
 * user's language, so this matches the "Snack {number}" name new meals get (#6204) in every locale.
 */
function firstFreeMealName(name: string, namesInUse: Set<string>): string {
  const mealNumber = /^Meal (\d+)$/;
  if (mealNumber.test(name)) {
    const highest = Math.max(...[...namesInUse].map(n => Number(mealNumber.exec(n)?.[1] ?? 0)));
    return `Meal ${highest + 1}`;
  }
  const base = name.replace(/ \d+$/, '');
  let number = 2;
  while (namesInUse.has(`${base} ${number}`)) {
    ++number;
  }
  return `${base} ${number}`;
}

/**
 * Give a meal hidden behind another meal of the same name its own free name, so it shows again and
 * counts in the day's total (#6207). Changes `name` in place and returns what it renamed; the
 * caller saves those meals.
 *
 * Only a hidden meal that holds food (`mealWouldCountTowardDayTotal`) AND was added by hand is
 * renamed. Two meals added by hand under one name are two meals -- that was #6204's naming bug.
 * Two schedule-built meals under one name can be two copies of ONE meal (the #4728 customer's
 * client and coach copies), which must keep counting once, so those are left as they are. When
 * such a pair was built in two time zones, its drop is reported under its own name (#6879,
 * `describeDroppedDuplicateMeals` in the client), because it can also be two different meals.
 *
 * Renames in `pickOneMealPerSlot`'s own order, so every device picks the same names, and a day it
 * already repaired gives nothing to rename. See design-docs/sync-integrity.md, "One Rule for Which
 * Day an Entry Belongs To, and One Meal per Slot".
 */
export function renameHiddenDuplicateMeals(dayMeals: Log_Meal[], referenceTime: DateTime): { meal: Log_Meal; oldName: string }[] {
  const { dropped } = pickOneMealPerSlot(dayMeals, referenceTime);
  const toRename = dropped.filter(meal => mealWouldCountTowardDayTotal(meal) && mealWasAddedByHand(meal));
  if (toRename.length === 0) return [];

  // rename in the picker's own order (wall clock, then _id), so the numbering does not depend on
  // the order the caller passed the meals in
  const inSortedOrder = [...toRename].sort((a, b) => {
    const aTime = getEntryLocalTimeInDesiredTimeZone(a, referenceTime).toMillis();
    const bTime = getEntryLocalTimeInDesiredTimeZone(b, referenceTime).toMillis();
    return aTime !== bTime ? aTime - bTime : (a._id < b._id ? -1 : 1);
  });

  const namesInUse = new Set(dayMeals.map(meal => meal.name));
  const renamed: { meal: Log_Meal; oldName: string }[] = [];
  for (const meal of inSortedOrder) {
    const oldName = meal.name;
    meal.name = firstFreeMealName(oldName, namesInUse);
    namesInUse.add(meal.name);
    renamed.push({ meal, oldName });
  }
  return renamed;
}

/**
 * Put meals copied from another day onto a day's meals, and settle the id each copy is saved under.
 *
 * - if the day has a meal of the same name, the copy replaces it and takes over its `_id` and
 *   `_rev`, so saving the copy really overwrites that meal instead of adding a second one
 * - otherwise the copy is added under its natural id, or the first free one, and as a new document
 *   it carries no `_rev`
 *
 * A copy's natural id is built from its new time, and a meal that was moved keeps the id of the
 * time it was created at — so without this a copy could take a different meal's id and be saved
 * over it, food included (#4641's breakfast holds the 12:30 id). Why the ids work this way:
 * design-docs/sync-integrity.md, "A Fresh Entry Must Never Take an Id Another Entry Holds".
 *
 * `otherTakenIds` holds ids that are taken by meals NOT on this day's list. A meal's id names the
 * instant it was created at, while the day it is shown on comes from the wall-clock time on it, so
 * after a time-zone change or a move across midnight a meal filed on this day can be shown on a
 * neighbouring one and never reach `dayMeals` (#5466). A copy avoids those ids too. The same-name
 * takeover ignores the set: taking that meal's id is a deliberate overwrite of its document.
 *
 * The copies are changed in place, so a caller that tracks copies by id must read their ids again
 * afterwards. Two copies can arrive with one id (two meals copied to the same time), so an old id
 * does not say which copy it was.
 */
export function placeCopiedMealsOnDay(dayMeals: Log_Meal[], copies: Log_Meal[], otherTakenIds?: ReadonlySet<string>): Log_Meal[] {
  const meals = [...dayMeals];
  for (const copy of copies) {
    const existingIndex = copy.name ? meals.findIndex(m => m.name === copy.name) : -1;
    if (existingIndex >= 0) {
      const replaced = meals[existingIndex];
      copy._id = replaced._id;
      if (replaced._rev) {
        copy._rev = replaced._rev;
      } else {
        delete copy._rev;
      }
      meals[existingIndex] = copy;
    } else {
      const takenIds = new Set([...meals.map(m => m._id), ...(otherTakenIds ?? [])]);
      copy._id = firstFreeLogEntryId(copy.time_utc, copy.type, takenIds);
      delete copy._rev;
      meals.push(copy);
    }
  }
  return meals;
}
